import { useCallback } from 'react';
import { useAppToast } from '@/hooks/useAppToast';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { useFCMToken } from '@/hooks/useFCMToken';
import { ContactLogic } from '@/modules/contact';
import { useContactState } from '@/modules/contact/ContactStateContext';
import {
  contactEnrichmentsForLeg,
  getReturnNotificationBody,
  scheduleFCMReturnNotification,
} from '@/modules/share/useShareController';
import { recordSentLegAwareness } from '@/modules/share/useTopicAwareness';
import { MePrFoService } from '@/services/meprfo';
import { OutboxLogic } from './logic';
import { useOutbox } from './OutboxContext';
import type { OutboxAction, OutboxSendChange } from './reducer';
import type { OutboxItem, OutboxSend } from './types';

export interface OutboxSender {
  /**
   * Hand the message at `index` to the SMS app, creating its leg the first
   * time. Resolves false when the leg could not be created, which it has told
   * the sender about.
   */
  openText: (index: number) => Promise<boolean>;
  /**
   * The sender says the awaiting message went. False when that failed to
   * record.
   */
  confirm: (index: number) => Promise<boolean>;
  shareLater: (index: number) => Promise<void>;
  markSent: (index: number) => Promise<void>;
  markNotSent: (index: number) => Promise<void>;
  remove: (index: number) => Promise<void>;
  markAllSent: () => Promise<void>;
  markAllNotSent: () => Promise<void>;
  clear: () => Promise<void>;
}

/**
 * The outbox's send machine for one send: every change the sender can make to
 * where its messages stand, with the server told what it needs to know.
 *
 * A message's leg is created the first time the message is opened and reused
 * ever after, so trying again and resending send the same link. Only confirming
 * marks the leg sent; marking a sent message not sent puts its leg back in
 * progress. Only a message that has been opened can be marked sent: one never
 * opened has no leg, and nothing of it has gone out.
 */
export function useOutboxSender(sendId: string | null): OutboxSender {
  const { dispatch: dispatchOutbox, getSend } = useOutbox();
  const { dispatch } = useContactState();
  const deviceInfo = useDeviceInfo();
  const fcmToken = useFCMToken();
  const [presentToast] = useAppToast();

  /**
   * Apply a change to this send. Resolves once stored, with the send as
   * changed, or null when there is no such send.
   */
  const change = useCallback(
    async (action: OutboxSendChange): Promise<OutboxSend | null> => {
      if (!sendId) return null;
      const next = await dispatchOutbox({ ...action, sendId } as OutboxAction);
      return next.sends.find(send => send.id === sendId) ?? null;
    },
    [dispatchOutbox, sendId]
  );

  const reportError = useCallback(
    (message: string, error: unknown) => {
      presentToast({ message, duration: 3000, color: 'danger' });
      // eslint-disable-next-line no-console
      console.error(message, error);
    },
    [presentToast]
  );

  const handOff = useCallback(
    async (send: OutboxSend, item: OutboxItem, hasMoreToShare: boolean) => {
      const smsLink = ContactLogic.createSMSLink(
        item.recipients,
        OutboxLogic.messageFor(item),
        deviceInfo
      );
      window.dispatchEvent(
        new CustomEvent('share-wizard:sms-link', { detail: { smsLink } })
      );
      if ('Cypress' in window) return;
      scheduleFCMReturnNotification(
        fcmToken,
        'Tap to return from sending',
        getReturnNotificationBody(item.leg.intent, hasMoreToShare),
        `${window.location.origin}/topics/${encodeURIComponent(send.topicId)}`
      ).catch(() => {});
      if (import.meta.env.MODE === 'test') return;
      // Let the SendingOverlay the event above shows paint before handing off,
      // so it is what the app switcher captures and what greets the sender on
      // the way back. Two frames guarantee a paint.
      await new Promise<void>(resolve =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );
      window.location.href = smsLink;
    },
    [deviceInfo, fcmToken]
  );

  const openText = useCallback(
    async (index: number) => {
      if (!sendId) return false;
      const send = getSend(sendId);
      const item = send?.items[index];
      if (!send || !item) return false;

      let legId = item.legId;
      if (!legId) {
        try {
          const result = await MePrFoService.initiateSharingLeg({
            ...item.leg,
            recipients: item.recipients.map(contact =>
              ContactLogic.toContactLike(contact)
            ),
          });
          legId = result.leg.id;
          const enrichments = contactEnrichmentsForLeg(item.recipients, result);
          if (enrichments.length > 0) {
            dispatch({ type: 'ENRICH_CONTACTS', payload: enrichments });
          }
          const info = result.reshareInfo;
          if (info) {
            await change({
              type: 'SET_RUN_PROGRESS',
              runProgress: {
                runImpressionCount: info.runImpressionCount,
                impressionGoal: info.impressionGoal ?? null,
              },
            });
          }
        } catch (error) {
          reportError("Couldn't open this text. Please try again.", error);
          return false;
        }
      }

      // Stored before the handoff: the OS may suspend the webview as soon as
      // Messages comes up, and the sender coming back has to find this message
      // waiting on them.
      const opened = await change({ type: 'OPEN_ITEM', index, legId });
      if (!opened) return false;
      await handOff(
        opened,
        opened.items[index],
        OutboxLogic.upNextIndex(opened) !== -1
      );
      return true;
    },
    [change, dispatch, getSend, handOff, reportError, sendId]
  );

  const recordSent = useCallback(async (send: OutboxSend, item: OutboxItem) => {
    if (item.legId) await MePrFoService.markLegAsSent(item.legId);
    recordSentLegAwareness(send.topicId, item.leg.intent, item.recipients);
  }, []);

  const confirm = useCallback(
    async (index: number) => {
      const send = sendId ? getSend(sendId) : null;
      const item = send?.items[index];
      if (!send || !item) return false;
      if (!item.resending) {
        try {
          await recordSent(send, item);
        } catch (error) {
          reportError("Couldn't record that text as sent.", error);
          return false;
        }
      }
      await change({ type: 'CONFIRM', index });
      return true;
    },
    [change, getSend, recordSent, reportError, sendId]
  );

  const markSent = useCallback(
    async (index: number) => {
      const send = sendId ? getSend(sendId) : null;
      const item = send?.items[index];
      if (!send || !item || !OutboxLogic.canMarkSent(item)) return;
      if (item.state === 'awaitingConfirm' && item.resending) {
        await change({ type: 'CONFIRM', index });
        return;
      }
      try {
        await recordSent(send, item);
      } catch (error) {
        reportError("Couldn't record that text as sent.", error);
        return;
      }
      await change({ type: 'MARK_SENT', index });
    },
    [change, getSend, recordSent, reportError, sendId]
  );

  const markNotSent = useCallback(
    async (index: number) => {
      const item = sendId ? getSend(sendId)?.items[index] : undefined;
      if (!item) return;
      if (item.legId && (item.state === 'sent' || item.resending)) {
        try {
          await MePrFoService.markLegAsInProgress(item.legId);
        } catch (error) {
          reportError("Couldn't mark that text as not sent.", error);
          return;
        }
      }
      await change({ type: 'MARK_NOT_SENT', index });
    },
    [change, getSend, reportError, sendId]
  );

  const markAllSent = useCallback(async () => {
    const send = sendId ? getSend(sendId) : null;
    if (!send) return;
    try {
      for (const item of send.items) {
        if (OutboxLogic.canMarkSent(item) && !item.resending) {
          await recordSent(send, item);
        }
      }
    } catch (error) {
      reportError("Couldn't record every text as sent.", error);
      return;
    }
    await change({ type: 'MARK_ALL_SENT' });
  }, [change, getSend, recordSent, reportError, sendId]);

  const markAllNotSent = useCallback(async () => {
    const send = sendId ? getSend(sendId) : null;
    if (!send) return;
    try {
      for (const item of send.items) {
        if (item.legId && (item.state === 'sent' || item.resending)) {
          await MePrFoService.markLegAsInProgress(item.legId);
        }
      }
    } catch (error) {
      reportError("Couldn't mark every text as not sent.", error);
      return;
    }
    await change({ type: 'MARK_ALL_NOT_SENT' });
  }, [change, getSend, reportError, sendId]);

  return {
    openText,
    confirm,
    shareLater: async index => {
      await change({ type: 'SHARE_LATER', index });
    },
    markSent,
    markNotSent,
    remove: async index => {
      await change({ type: 'REMOVE_ITEM', index });
    },
    markAllSent,
    markAllNotSent,
    clear: async () => {
      if (sendId) await dispatchOutbox({ type: 'REMOVE_SEND', sendId });
    },
  };
}
